import i18n from 'i18next';
import { normalizeLocale } from './config';
import { isJalaliPickerLocale } from '../utils/calendarPickerLocale';
import {
  formatJalaliIsoLabel,
  getIsoWeekNumber,
  gregorianToJalali,
  gregorianWeekdaysToJalali,
  jalaliMonths,
  toPersianDigits,
  weekDaysFa,
} from '../utils/jalali';

function parseIsoDate(iso: string): { y: number; m: number; d: number } | null {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return { y, m, d };
}

/** ISO `YYYY-MM-DD` label — Jalali for `fa`, locale-aware Gregorian otherwise. */
export function formatIsoDateLabel(iso: string, locale?: string): string {
  if (!iso) return '';
  const lang = locale ?? i18n.language ?? 'en';
  if (isJalaliPickerLocale(normalizeLocale(lang))) {
    return formatJalaliIsoLabel(iso);
  }
  try {
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso + 'T00:00:00'));
  } catch {
    return iso;
  }
}

/** Locale-aware short date (e.g. Jun 12 or ۱۵ خرداد). */
export function formatShortDate(date: Date | string, locale?: string): string {
  const lang = locale ?? i18n.language ?? 'en';
  if (typeof date === 'string') {
    const parsed = parseIsoDate(date);
    if (parsed && isJalaliPickerLocale(normalizeLocale(lang))) {
      const j = gregorianToJalali(parsed.y, parsed.m, parsed.d);
      return `${j.dat} ${jalaliMonths[j.month]}`;
    }
  }
  const d = typeof date === 'string' ? new Date(date + (date.length === 10 ? 'T00:00:00' : '')) : date;
  if (isJalaliPickerLocale(normalizeLocale(lang))) {
    const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
    return `${j.dat} ${jalaliMonths[j.month]}`;
  }
  return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(d);
}

/** Locale-aware date with weekday. */
export function formatWeekdayDate(date: Date | string, locale?: string): string {
  const lang = locale ?? i18n.language ?? 'en';
  const d = typeof date === 'string'
    ? new Date(date + (date.length === 10 ? 'T00:00:00' : ''))
    : date;
  if (isJalaliPickerLocale(normalizeLocale(lang))) {
    const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
    const weekday = weekDaysFa[gregorianWeekdaysToJalali(d.getDay())];
    return `${weekday} ${j.dat} ${jalaliMonths[j.month]}`;
  }
  return new Intl.DateTimeFormat(lang, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/** ISO week label from `YYYY-MM-DD` — e.g. Week 26 / هفته ۲۶. */
export function formatWeekOfYearFromISO(iso: string, locale?: string): string {
  const parsed = parseIsoDate(iso);
  if (!parsed) return '—';
  const week = getIsoWeekNumber(parsed.y, parsed.m, parsed.d);
  const lang = normalizeLocale(locale ?? i18n.language ?? 'en');
  if (isJalaliPickerLocale(lang)) {
    return `هفته ${toPersianDigits(week)}`;
  }
  return i18n.t('common.weekNumber', { lng: lang, week });
}

/** Week start with weekday — Jalali for fa, locale-aware otherwise. */
export function formatWeekStartFromISO(iso: string, locale?: string): string {
  if (!iso) return '—';
  return formatWeekdayDate(iso, locale);
}

/** Relative time labels via i18n keys under `common.relative`. */
export function formatRelativeTime(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return t('common.relative.justNow');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('common.relative.minutesAgo', { n: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('common.relative.hoursAgo', { n: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  return t('common.relative.daysAgo', { n: diffDay, plural: diffDay !== 1 ? 's' : '' });
}
